import { useCallback } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import type { CreateSpaceResponse } from '../../controllers/space-store/types';

import { type CreateSpaceRequest, createSpaceFetchAction } from './useCreateSpace.fetch';

type UseCreateSpaceProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	onSuccess?: (space: CreateSpaceResponse) => void;
	onError?: (error: Error) => void;
};

export const useCreateSpace = ({
	cloudId,
	product,
	experienceId,
	onSuccess,
	onError,
}: UseCreateSpaceProps): {
	mutate: (
		payload: CreateSpaceRequest,
		callbacks?:
			| {
					onSuccess?: ((data: CreateSpaceResponse) => void) | undefined;
					onError?: (error: ApiError) => void;
			  }
			| undefined,
	) => Promise<CreateSpaceResponse | undefined>;
	isSubmitting: boolean;
	error: ApiError | null;
} => {
	const mutationFn = useCallback(
		(request: CreateSpaceRequest) =>
			createSpaceFetchAction({
				request,
				cloudId,
				product,
				experienceId,
			}),
		[cloudId, product, experienceId],
	);

	const { mutate, isSubmitting, error } = useMutation<CreateSpaceRequest, CreateSpaceResponse>({
		mutationFn,
		onSuccess,
		onError,
	});

	return { mutate, isSubmitting, error };
};
